import { useState, useEffect } from 'react';
import axios from 'axios';
import { Users, Search, Building2, User, Hammer, Mail, Phone, MapPin } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CRM = () => {
  const [contacts, setContacts] = useState([]);
  const [filter, setFilter] = useState('builder');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${BACKEND_URL}/api/contacts?contact_type=${filter}`);
        setContacts(res.data || []);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [filter]);

  const filtered = contacts.filter(c => 
    c.name?.toLowerCase().includes(search.toLowerCase()) || 
    c.company?.toLowerCase().includes(search.toLowerCase()) ||
    c.city?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="max-w-7xl mx-auto p-8 space-y-12">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-black tracking-tight">Intelligence Hub</h1>
          <p className="text-sm text-muted-foreground mt-2">Manage your high-value contacts and builder networks.</p>
        </div>
      </div>

      <div className="flex flex-col md:flex-row gap-4 items-center">
        <Tabs value={filter} onValueChange={setFilter} className="w-full md:w-auto">
          <TabsList className="grid grid-cols-3 w-full md:w-[400px] h-12">
            <TabsTrigger value="builder" className="gap-2"><Hammer size={16}/> Builders</TabsTrigger>
            <TabsTrigger value="seller" className="gap-2"><User size={16}/> Sellers</TabsTrigger>
            <TabsTrigger value="buyer" className="gap-2"><Building2 size={16}/> Buyers</TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
          <Input 
            placeholder="Search by name, company, or city..." 
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-12 h-12 text-lg border-2"
          />
        </div>
      </div>

      {loading ? (
        <div className="text-center py-20 animate-pulse text-sm font-bold">Synchronizing Database...</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.slice(0, 100).map((contact, idx) => (
            <Card key={idx} className="border-2 hover:border-primary transition-all group shadow-sm bg-card">
              <CardHeader className="pb-2">
                <div className="flex justify-between items-start">
                  <div className="space-y-1">
                    <CardTitle className="text-sm font-black">{contact.name}</CardTitle>
                    {contact.company && <p className="text-primary font-bold text-sm uppercase tracking-tighter">{contact.company}</p>}
                  </div>
                  <Badge variant="secondary" className="bg-muted text-foreground">{contact.contact_type}</Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-4 pt-4">
                <div className="space-y-2 text-sm font-medium">
                  {contact.phone && <div className="flex items-center gap-3 text-muted-foreground group-hover:text-foreground"><Phone size={14}/> {contact.phone}</div>}
                  {contact.email && <div className="flex items-center gap-3 text-muted-foreground group-hover:text-foreground"><Mail size={14}/> {contact.email}</div>}
                  {(contact.city || contact.state) && <div className="flex items-center gap-3 text-muted-foreground group-hover:text-foreground"><MapPin size={14}/> {contact.city}, {contact.state}</div>}
                </div>
                <Button variant="outline" className="w-full h-11 border-2 font-bold group-hover:bg-primary group-hover:text-white transition-colors uppercase tracking-widest text-xs">View Full Profile</Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
      
      {!loading && filtered.length === 0 && (
        <div className="text-center py-40 opacity-20 flex flex-col items-center">
          <Users size={80} />
          <p className="mt-4 text-2xl font-black">Zero matches found.</p>
        </div>
      )}
    </div>
  );
};

export default CRM;
